<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import * as XLSX from 'xlsx'
import download from 'downloadjs'
import { useToast } from 'primevue/usetoast'
import '../../assets/scss/attendance.scss'
import AttendanceDashboard from './AttendanceDashboard.vue'
import AttendanceModal from './AttendanceModal.vue'
import {
  useAttendanceReportStore,
  AttendanceActionTypes,
} from '@/modules/backoffice/stores/attendance.store.ts'
import { ATTENDANCE_STATUS_LABELS } from '@/modules/backoffice/constants/attendance.const.ts'
import {
  isExportRestShift,
  isFaultAttendanceForRestExport,
} from '@/modules/backoffice/utils/attendance-export-rest.ts'

const store = useAttendanceReportStore()

const now = new Date()
const month = ref<number>(now.getMonth() + 1)
const year = ref<number>(now.getFullYear())
const showDepartmentColumn = ref(false)
const selectedDate = ref(new Date(year.value, month.value - 1))

watch(selectedDate, async (newDate: Date | null) => {
  if (newDate) {
    const newMonth = newDate.getMonth() + 1
    const newYear = newDate.getFullYear()

    if (newMonth !== month.value || newYear !== year.value) {
      month.value = newMonth
      year.value = newYear
      await store.fetchMatrixList(month.value, year.value)
      await store.fetchDashboard(month.value, year.value)
    }
  }
})

const days = computed(() => {
  const numDays = new Date(year.value, month.value, 0).getDate()
  return Array.from({ length: numDays }, (_, i) => i + 1)
})
const hoveredDay = ref<number | null>(null)

const daysWithWork = computed(() => {
  const map: Record<number, { am: boolean; pm: boolean }> = {}
  days.value.forEach((day) => {
    map[day] = {
      am: store.matrixList.some((emp) => {
        const val = emp.attendance?.[day]?.am
        return val !== undefined && val !== null && val !== 0 && val !== '' && val !== 'NONE'
      }),
      pm: store.matrixList.some((emp) => {
        const val = emp.attendance?.[day]?.pm
        return val !== undefined && val !== null && val !== 0 && val !== '' && val !== 'NONE'
      }),
    }
  })
  return map
})

const isDisabled = (day: number, shift: 'am' | 'pm') => {
  const date = new Date(year.value, month.value - 1, day)
  const dayOfWeek = date.getDay()

  // Xác định xem có phải là ngày nghỉ mặc định không (CN hoặc chiều Thứ 7)
  const isDefaultWeekend = dayOfWeek === 0 || (dayOfWeek === 6 && shift === 'pm')

  // Nếu không phải ngày nghỉ mặc định thì là ngày đi làm (không disable)
  if (!isDefaultWeekend) return false

  // Nếu là ngày nghỉ mặc định, chỉ disable nếu KHÔNG có ai đi làm/tăng ca
  return !daysWithWork.value[day]?.[shift]
}

const getCellClass = (val: string | number | null) => {
  if (val === null || val === '' || val === 0 || val === 'NONE') return 'cell-status-0'
  if (val === 'FULLTIME' || val === 'FULL' || val === 1) return 'cell-status-1'
  if (
    [
      'MISSING_TIME',
      'MISSING',
      'MORNING_MISSING',
      'AFTERNOON_MISSING',
      'MORNING_MISSING_PAID',
      'AFTERNOON_MISSING_PAID',
    ].includes(String(val)) ||
    val === 2
  )
    return 'cell-status-2'
  if (
    ['PAID_LEAVE', 'MORNING_PAID_LEAVE', 'AFTERNOON_PAID_LEAVE'].includes(String(val)) ||
    val === 3
  )
    return 'cell-status-3'
  if (['ABSENT', 'MORNING_ABSENT', 'AFTERNOON_ABSENT'].includes(String(val)) || val === 4)
    return 'cell-status-4'
  if (val === 'OT_APPROVED' || val === 'OT' || val === 5) return 'cell-status-5'
  if (val === 6) return 'cell-status-6'
  return ''
}

const getCheckboxClass = (val: string | number | null) => {
  const cls = getCellClass(val)
  return cls ? cls.replace('cell-', '') : ''
}

const isCheckboxChecked = (val: string | number | null) => {
  const cls = getCellClass(val)
  return ['cell-status-1', 'cell-status-2', 'cell-status-5', 'cell-status-6'].includes(cls)
}

const collapsedGroups = ref<Set<string>>(new Set())

const toggleGroup = (deptName: string) => {
  if (collapsedGroups.value.has(deptName)) {
    collapsedGroups.value.delete(deptName)
  } else {
    collapsedGroups.value.add(deptName)
  }
}

const groupedEmployees = computed(() => {
  const groups: Record<string, typeof store.matrixList> = {}
  store.matrixList.forEach((emp) => {
    if (!groups[emp.department]) groups[emp.department] = []
    groups[emp.department].push(emp)
  })
  return groups
})

const toast = useToast()
const exportingExcel = ref(false)

const formatAttendanceCellForExport = (val: string | number | null | undefined): string => {
  if (val === null || val === undefined || val === '' || val === 0 || val === 'NONE') {
    return ''
  }
  const s = String(val)
  if (ATTENDANCE_STATUS_LABELS[s]) return ATTENDANCE_STATUS_LABELS[s]
  const numericMap: Record<string, string> = {
    '1': ATTENDANCE_STATUS_LABELS['FULLTIME'] ?? 'Đủ công',
    '2': 'Thiếu giờ',
    '3': 'Nghỉ có phép',
    '4': 'Vắng không phép',
    '5': ATTENDANCE_STATUS_LABELS['OT_APPROVED'] ?? 'Tăng ca',
    '6': 'Ghi nhận khác',
  }
  return numericMap[s] ?? s
}

/** Trên ngày nghỉ (CN, T7 chiều, lễ VN): không xuất thiếu giờ / vắng — để trống. */
const formatAttendanceCellForExportWithRest = (
  y: number,
  m: number,
  day: number,
  shift: 'am' | 'pm',
  val: string | number | null | undefined,
): string => {
  if (isExportRestShift(y, m, day, shift) && isFaultAttendanceForRestExport(val)) {
    return ''
  }
  return formatAttendanceCellForExport(val)
}

const handleExportExcel = () => {
  if (store.matrixList.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'Thông báo',
      detail: 'Không có dữ liệu để xuất.',
      life: 3000,
    })
    return
  }
  exportingExcel.value = true
  try {
    const header: string[] = ['Mã NV', 'Phòng ban', 'Tên NV']
    for (const day of days.value) {
      header.push(`${String(day).padStart(2, '0')} S`)
      header.push(`${String(day).padStart(2, '0')} C`)
    }
    header.push('Công', 'OT15', 'OT20')

    const rows: string[][] = [header]
    for (const members of Object.values(groupedEmployees.value)) {
      for (const emp of members) {
        if (!emp?.uuid) continue
        const row: string[] = [
          String(emp.code ?? ''),
          String(emp.department ?? ''),
          String(emp.name ?? ''),
        ]
        for (const day of days.value) {
          row.push(
            formatAttendanceCellForExportWithRest(
              year.value,
              month.value,
              day,
              'am',
              emp.attendance?.[day]?.am,
            ),
          )
          row.push(
            formatAttendanceCellForExportWithRest(
              year.value,
              month.value,
              day,
              'pm',
              emp.attendance?.[day]?.pm,
            ),
          )
        }
        row.push(String(emp.summary?.work ?? 0))
        row.push(String(emp.summary?.ot15 ?? 0))
        row.push(String(emp.summary?.ot20 ?? 0))
        rows.push(row)
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Chấm công')
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const filename = `cham-cong_thang-${String(month.value).padStart(2, '0')}_${year.value}.xlsx`
    download(
      buf,
      filename,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    )
    toast.add({
      severity: 'success',
      summary: 'Thành công',
      detail: 'Đã tải file Excel.',
      life: 3000,
    })
  } catch (e) {
    console.error(e)
    toast.add({
      severity: 'error',
      summary: 'Lỗi',
      detail: 'Không thể xuất file Excel.',
      life: 4000,
    })
  } finally {
    exportingExcel.value = false
  }
}

const isModalVisible = ref(false)
const selectedDay = ref<number | null>(null)
const selectedEmp = ref<any>(null)

const handleCheckClick = async (emp: any, day: number, _shift: 'am' | 'pm') => {
  selectedEmp.value = emp
  selectedDay.value = day
  // Gọi API 3: Fetch chi tiết ngày trước khi mở Modal
  await store.fetchDayDetail(emp.uuid, day, month.value, year.value)
  isModalVisible.value = true
}

const handleSaveAttendance = async (payload: any) => {
  if (selectedEmp.value && selectedDay.value) {
    await store.updateHRRecord(selectedEmp.value.id, selectedDay.value, payload)
  }
}

// Fetch initial data
onMounted(async () => {
  await store.fetchMatrixList(month.value, year.value)
  await store.fetchDashboard(month.value, year.value)
})

// === Drag-to-Scroll ===
const tableContainerRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const scrollStartX = ref(0)
const scrollStartY = ref(0)

const onDragStart = (e: MouseEvent) => {
  const container = tableContainerRef.value
  if (!container) return
  // Ignore if clicked on an interactive element
  const target = e.target as HTMLElement
  if (target.closest('input, label, button, a, .att-checkbox')) return
  isDragging.value = true
  dragStartX.value = e.pageX
  dragStartY.value = e.pageY
  scrollStartX.value = container.scrollLeft
  scrollStartY.value = container.scrollTop
  container.style.cursor = 'grabbing'
  container.style.userSelect = 'none'
}

const onDragMove = (e: MouseEvent) => {
  if (!isDragging.value || !tableContainerRef.value) return
  e.preventDefault()
  const dx = e.pageX - dragStartX.value
  const dy = e.pageY - dragStartY.value
  tableContainerRef.value.scrollLeft = scrollStartX.value - dx
  tableContainerRef.value.scrollTop = scrollStartY.value - dy
}

const onDragEnd = () => {
  isDragging.value = false
  if (tableContainerRef.value) {
    tableContainerRef.value.style.cursor = 'grab'
    tableContainerRef.value.style.removeProperty('user-select')
  }
}
</script>

<template>
  <div class="attendance-container bg-slate-50/50 flex flex-col items-center">
    <div class="w-full relative">
      <!-- Giao diện Header 1 dòng Tối ưu (Title + Filter + Legend) -->
      <div
        class="mb-4 bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex flex-wrap lg:flex-nowrap items-center justify-between gap-4"
      >
        <!-- Left: Title & Filter -->
        <div class="flex items-center gap-3 shrink-0">
          <div class="flex items-center gap-2">
            <span class="text-base text-sky-500">📅</span>
            <h2 class="text-sm font-black text-slate-800 uppercase tracking-widest hidden sm:block">
              Bảng Công
            </h2>
          </div>
          <div class="h-5 w-px bg-slate-200 hidden sm:block"></div>

          <!-- Month Filter -->
          <div class="relative flex items-center">
            <DatePicker
              v-model="selectedDate"
              view="month"
              dateFormat="mm/yy"
              class="w-[120px] scale-90 origin-left"
              :pt="{
                input: {
                  class:
                    'text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-md outline-none hover:bg-sky-100 transition-colors focus:ring-2 focus:ring-sky-500/20 shadow-inner py-1.5 px-3',
                },
              }"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            class="shrink-0 text-xs border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            :disabled="store.matrixList.length === 0 || exportingExcel"
            @click="handleExportExcel"
          >
            <i
              :class="exportingExcel ? 'pi pi-spinner animate-spin' : 'pi pi-file-excel'"
              class="text-sm text-emerald-600"
              aria-hidden="true"
            />
            Xuất Excel
          </Button>
        </div>

        <!-- Right: Compact Legend (ẩn chữ trên mobile) -->
        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] sm:text-[11px] font-semibold text-slate-600"
        >
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-1 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" checked class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Đủ công</span>
          </div>
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-2 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" checked class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Thiếu giờ</span>
          </div>
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-5 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" checked class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Tăng ca (OT)</span>
          </div>
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-3 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Có phép</span>
          </div>
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-4 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Không phép</span>
          </div>
          <div class="flex items-center gap-1.5">
            <label
              class="att-checkbox status-0 m-0 pointer-events-none scale-75 transform origin-left"
              ><input type="checkbox" class="sr-only" /><span
                class="checkmark shadow-sm"
              ></span></label
            ><span class="hidden xl:inline">Chưa chốt</span>
          </div>
        </div>
      </div>

      <!-- Dashboard UI (tự gọi API riêng qua store) -->
      <template v-if="store.action_type === AttendanceActionTypes.FETCHING">
        <div class="text-center py-20 bg-white rounded-2xl border border-slate-100 mb-8">
          <div
            class="inline-block animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full mb-3"
          ></div>
          <p class="text-slate-500 font-medium">Đang tải dữ liệu chấm công từ hệ thống...</p>
        </div>
      </template>

      <template v-else-if="store.matrixList.length > 0">
        <AttendanceDashboard />
      </template>

      <template v-else>
        <div class="text-center py-20 bg-white rounded-2xl border border-slate-100 mb-8">
          <p class="text-slate-500 font-medium">Không tìm thấy dữ liệu chấm công cho tháng này.</p>
        </div>
      </template>

      <div
        ref="tableContainerRef"
        class="table-container bg-white border-slate-200"
        style="cursor: grab"
        v-if="store.matrixList.length > 0"
        @mousedown="onDragStart"
        @mousemove="onDragMove"
        @mouseup="onDragEnd"
        @mouseleave="onDragEnd"
      >
        <table class="excel-table w-full">
          <thead>
            <tr>
              <!-- TH 1: MÃ NV -->
              <th rowspan="3" class="sticky-col col-id z-[40]">Mã NV</th>
              <!-- TH 2: PHÒNG BAN -->
              <th v-if="showDepartmentColumn" rowspan="3" class="sticky-col col-dept z-[40]">
                Phòng ban
              </th>
              <!-- TH 3: TÊN NV -->
              <th
                rowspan="3"
                class="sticky-col col-name z-[40]"
                :style="{ left: showDepartmentColumn ? '230px' : '90px' }"
              >
                Tên NV
              </th>

              <th :colspan="days.length * 2" class="month-header">Tháng {{ month }}/{{ year }}</th>

              <th colspan="3" class="summary-header">Tổng kết</th>
            </tr>
            <tr>
              <template v-for="day in days" :key="'day-' + day">
                <th
                  colspan="2"
                  class="day-col day-end work_day text-center"
                  :class="{
                    weekend: isDisabled(day, 'am') && isDisabled(day, 'pm'),
                    'hover-col': hoveredDay === day,
                  }"
                  @mouseenter="hoveredDay = day"
                  @mouseleave="hoveredDay = null"
                >
                  <span class="day-num font-black block mb-1 text-sm">{{ day }}</span>
                  <span
                    class="day-name text-[10px] font-bold uppercase tracking-widest block text-slate-400"
                    >{{
                      ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'][
                        new Date(year, month - 1, day).getDay()
                      ]
                    }}</span
                  >
                </th>
              </template>

              <th rowspan="2" class="summary text-sky-600">Công</th>
              <th rowspan="2" class="summary text-purple-600">OT15</th>
              <th rowspan="2" class="summary text-rose-600">OT20</th>
            </tr>
            <tr class="text-[10px] font-bold uppercase">
              <template v-for="day in days" :key="`header-shifts-${day}`">
                <th
                  class="shift-col work_day"
                  :class="{ disabled: isDisabled(day, 'am'), 'hover-col': hoveredDay === day }"
                  @mouseenter="hoveredDay = day"
                  @mouseleave="hoveredDay = null"
                >
                  S
                </th>
                <th
                  class="shift-col work_day day-end"
                  :class="{ disabled: isDisabled(day, 'pm'), 'hover-col': hoveredDay === day }"
                  @mouseenter="hoveredDay = day"
                  @mouseleave="hoveredDay = null"
                >
                  C
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-for="(members, deptName) in groupedEmployees" :key="deptName">
              <tr class="group-header-row group" @click="toggleGroup(deptName as string)">
                <td
                  :colspan="showDepartmentColumn ? 3 : 2"
                  class="group-sticky-cell uppercase py-3 border-b border-slate-200 text-sm"
                >
                  <div class="flex items-center">
                    <span
                      class="toggle-icon transition-transform transform group-hover:scale-110"
                      :class="{ '-rotate-90': collapsedGroups.has(deptName as string) }"
                      >▼</span
                    >
                    <strong class="group-name">{{ deptName }}</strong>
                    <span class="emp-count">({{ members.length }} nhân sự)</span>
                  </div>
                </td>
                <td
                  :colspan="days.length * 2 + 3"
                  class="group-rest-cell border-b border-slate-200"
                ></td>
              </tr>
              <template v-if="!collapsedGroups.has(deptName as string)">
                <template v-for="emp in members">
                  <tr
                    class="hover:bg-sky-50 transition-colors group/row"
                    :key="emp.id"
                    v-if="emp?.uuid"
                  >
                    <!-- CỘT 1: ID -->
                    <td class="sticky-col col-id align-middle">
                      <span
                        class="text-[11px] font-bold text-slate-500 font-mono block w-full px-2"
                        >{{ emp.code }}</span
                      >
                    </td>

                    <!-- CỘT 2: PHÒNG BAN -->
                    <td v-if="showDepartmentColumn" class="sticky-col col-dept align-middle">
                      <span
                        class="text-[10px] font-semibold text-slate-600 bg-slate-50 group-hover/row:bg-white px-2 py-0.5 rounded border border-slate-100 uppercase tracking-widest mx-auto inline-block"
                        >{{ emp.department }}</span
                      >
                    </td>

                    <!-- CỘT 3: TÊN -->
                    <td
                      class="sticky-col col-name align-middle"
                      :style="{ left: showDepartmentColumn ? '230px' : '90px' }"
                    >
                      <div class="flex items-center gap-3">
                        <img
                          :src="`https://api.dicebear.com/7.x/avataaars/svg?seed=${emp.code}`"
                          class="w-7 h-7 rounded-full bg-slate-100 ring-2 ring-white shadow-sm shrink-0"
                          alt="Avatar"
                        />
                        <span class="font-bold text-slate-800 text-[13px] truncate pr-4">{{
                          emp.name
                        }}</span>
                      </div>
                    </td>

                    <template v-for="day in days" :key="`${emp.id}-${day}`">
                      <!-- Sáng -->
                      <td
                        class="cell work_day relative"
                        :class="[
                          getCellClass(emp.attendance?.[day]?.am),
                          { disabled: isDisabled(day, 'am'), 'hover-col': hoveredDay === day },
                        ]"
                        @mouseenter="hoveredDay = day"
                        @mouseleave="hoveredDay = null"
                      >
                        <label
                          v-if="
                            emp.attendance?.[day]?.am !== undefined &&
                            emp.attendance?.[day]?.am !== null
                          "
                          class="att-checkbox cursor-pointer relative"
                          :class="getCheckboxClass(emp.attendance[day].am)"
                          @click.prevent="handleCheckClick(emp, day, 'am')"
                        >
                          <input
                            type="checkbox"
                            :checked="isCheckboxChecked(emp.attendance[day].am)"
                            class="sr-only"
                          />
                          <span class="checkmark">
                            <i
                              v-if="isCheckboxChecked(emp.attendance[day].am)"
                              class="fas fa-check text-[10px] m-auto mb-[2px]"
                            ></i>
                          </span>
                        </label>
                      </td>

                      <!-- Chiều -->
                      <td
                        class="cell work_day day-end relative"
                        :class="[
                          getCellClass(emp.attendance?.[day]?.pm),
                          { disabled: isDisabled(day, 'pm'), 'hover-col': hoveredDay === day },
                        ]"
                        @mouseenter="hoveredDay = day"
                        @mouseleave="hoveredDay = null"
                      >
                        <label
                          v-if="
                            emp.attendance?.[day]?.pm !== undefined &&
                            emp.attendance?.[day]?.pm !== null
                          "
                          class="att-checkbox cursor-pointer relative"
                          :class="getCheckboxClass(emp.attendance[day].pm)"
                          @click.prevent="handleCheckClick(emp, day, 'pm')"
                        >
                          <input
                            type="checkbox"
                            :checked="isCheckboxChecked(emp.attendance[day].pm)"
                            class="sr-only"
                          />
                          <span class="checkmark">
                            <i
                              v-if="isCheckboxChecked(emp.attendance[day].pm)"
                              class="fas fa-check text-[10px] m-auto mb-[2px]"
                            ></i>
                          </span>
                        </label>
                      </td>
                    </template>

                    <!-- Summary -->
                    <td class="summary align-middle">{{ emp.summary?.work || 0 }}</td>
                    <td class="summary align-middle">{{ emp.summary?.ot15 || 0 }}</td>
                    <td class="summary align-middle pr-4">{{ emp.summary?.ot20 || 0 }}</td>
                  </tr>
                </template>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Import Modal Component -->
    <AttendanceModal
      v-model:isVisible="isModalVisible"
      :selectedEmp="selectedEmp"
      :selectedDay="selectedDay"
      :month="month"
      :year="year"
      @save="handleSaveAttendance"
    />
  </div>
</template>

<style scoped>
/* Inline styling classes for Tailwind fallback in case SCSS was removed */
.att-checkbox .checkmark i {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
